import React from 'react';

function BowlIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 48 54" fill="none" aria-label="Bowl" style={{ flexShrink: 0 }}>
      <circle cx="5.5" cy="16" r="3.8" stroke="#AAAAAA" strokeWidth="1.4" fill="none"/>
      <circle cx="42.5" cy="16" r="3.8" stroke="#AAAAAA" strokeWidth="1.4" fill="none"/>
      <path d="M7 13 Q24 8 41 13 L36 27 Q24 31 12 27 Z" fill="#C8C8C8" stroke="#A0A0A0" strokeWidth="0.8" strokeLinejoin="round"/>
      <ellipse cx="24" cy="13" rx="17" ry="3.5" fill="#E0E0E0" stroke="#A0A0A0" strokeWidth="0.7"/>
      <rect x="21.5" y="27" width="5" height="5" rx="1" fill="#C8C8C8"/>
      <ellipse cx="24" cy="33" rx="6.5" ry="3" fill="#D0D0D0"/>
      <rect x="14" y="36" width="20" height="3.5" rx="1" fill="#C0C0C0"/>
      <rect x="11" y="39.5" width="26" height="5" rx="2" fill="#B0B0B0"/>
    </svg>
  );
}

/**
 * Final standings for a completed TML event.
 * Columns: Pos · Player · R1 · R2 · Total. The leaderboard is already ordered by
 * the server (cumulative total → playoff winner → R2 points → countback), so
 * positions are sequential — ties are resolved, no shared positions. The
 * champion (1st) is highlighted in gold; the top bowl finisher is marked.
 */
export default function FinalLeaderboard({ leaderboard = [], rounds = [] }) {
  if (!leaderboard.length) return null;

  const twoRounds = rounds.length >= 2;
  const championId = leaderboard[0]?.player_id ?? null;
  const bowlWinnerId = leaderboard.find(p => p.is_bowl)?.player_id ?? null;

  return (
    <div>
      <h3 className="text-lg font-bold text-gray-900 mb-3">Final Leaderboard</h3>
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-4">
        {/* Header */}
        <div className="bg-gray-100 px-4 py-2 flex items-center text-xs font-semibold text-gray-600 uppercase tracking-wide">
          <div className="w-10 shrink-0 text-center">Pos</div>
          <div className="flex-1">Player</div>
          {twoRounds && <div className="w-12 shrink-0 text-center">R1</div>}
          {twoRounds && <div className="w-12 shrink-0 text-center">R2</div>}
          <div className="w-14 shrink-0 text-center">Total</div>
        </div>

        {leaderboard.map((player, index) => {
          const total = Number(player.total_points) || 0;
          const r2 = Number(player.current_round_points) || 0;
          const r1 = total - r2;
          const isChampion = player.player_id === championId;
          const isBowlWinner = player.player_id === bowlWinnerId;

          return (
            <div
              key={player.round_player_id}
              className={`px-4 py-2.5 flex items-center border-t border-gray-100 ${
                isChampion ? 'bg-amber-50' : ''
              }`}
            >
              <div className="w-10 shrink-0 text-center text-sm font-semibold text-gray-600">
                {isChampion ? '🏆' : index + 1}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className={`truncate ${isChampion ? 'font-bold text-gray-900' : 'font-semibold text-gray-900'}`}>
                    {player.player_name}
                  </span>
                  {player.is_bowl && <BowlIcon />}
                </div>
                {isChampion && (
                  <p className="text-xs font-semibold text-amber-700 mt-0.5">
                    Champion Golfer of the Year
                    {player.is_playoff_winner ? ' · winner after playoff' : ''}
                  </p>
                )}
                {isBowlWinner && (
                  <p className="text-xs font-semibold text-gray-500 mt-0.5">
                    Winner of the Bowl
                    {player.is_bowl_playoff_winner ? ' · winner after playoff' : ''}
                  </p>
                )}
              </div>

              {twoRounds && (
                <div className="w-12 shrink-0 text-center text-sm text-gray-500">{r1}</div>
              )}
              {twoRounds && (
                <div className="w-12 shrink-0 text-center text-sm text-gray-500">{r2}</div>
              )}
              <div className={`w-14 shrink-0 text-center font-bold ${isChampion ? 'text-amber-600' : 'text-red-600'}`}>
                {total}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
